import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Autocomplete from "@material-ui/lab/Autocomplete";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexWrap: "wrap" },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": { marginRight: theme.spacing(1) }
  },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const AppointmentSchema = Yup.object().shape({
  professionalId: Yup.number().required("Selecione o profissional"),
  startDate: Yup.string().required("Informe a data"),
  startTime: Yup.string().required("Informe o horário")
});

const AppointmentModal = ({ open, onClose, reload, appointmentId, initialSlot }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const initialState = {
    contactId: "",
    serviceId: "",
    locationId: "",
    professionalId: "",
    startDate: initialSlot ? moment(initialSlot.start).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
    startTime: initialSlot ? moment(initialSlot.start).format("HH:mm") : "09:00",
    durationMinutes: 30,
    status: "agendado",
    notes: "",
    value: ""
  };

  const [appointment, setAppointment] = useState(initialState);
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [serviceProfessionalLinks, setServiceProfessionalLinks] = useState([]);
  const [professionalLocationLinks, setProfessionalLocationLinks] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    async function loadOptions() {
      try {
        const [
          usersResponse,
          servicesResponse,
          locationsResponse,
          serviceProfessionalsResponse,
          professionalLocationsResponse
        ] = await Promise.all([
          api.get("/users/list"),
          api.get("/services"),
          api.get("/agenda-locations"),
          api.get("/service-professionals"),
          api.get("/professional-locations")
        ]);

        setProfessionals(
          usersResponse.data || []
        );

        setServices(
          servicesResponse.data || []
        );

        setLocations(
          locationsResponse.data || []
        );

        setServiceProfessionalLinks(
          serviceProfessionalsResponse.data || []
        );

        setProfessionalLocationLinks(
          professionalLocationsResponse.data || []
        );
      } catch (err) {
        toastError(err);
      }
    }

    loadOptions();
  }, []);

  useEffect(() => {
    async function fetchAppointment() {
      if (!appointmentId) {
        setAppointment(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/appointments`);
        const found = data.find(a => a.id === appointmentId);
        if (found) {
          setAppointment({
            contactId: found.contactId || "",
            professionalId: found.professionalId || "",
            serviceId: found.serviceId || "",
            locationId: found.locationId || "",
            startDate: moment(found.startTime).format("YYYY-MM-DD"),
            startTime: moment(found.startTime).format("HH:mm"),
            durationMinutes: moment(found.endTime).diff(moment(found.startTime), "minutes"),
            status: found.status || "agendado",
            notes: found.notes || "",
            value: found.value || ""
          });
          setSelectedContact(found.contact || null);
        }
      } catch (err) {
        toastError(err);
      }
    }
    fetchAppointment();
  }, [appointmentId, open]);

  const handleSearchContact = async value => {
    if (!value || value.length < 2) return;
    try {
      const { data } = await api.get("/contacts", { params: { searchParam: value } });
      setContactOptions(data.contacts || []);
    } catch (err) {
      toastError(err);
    }
  };

  const handleServiceChange = (
    serviceId,
    setFieldValue
  ) => {
    const service =
      services.find(
        s =>
          Number(s.id) ===
          Number(serviceId)
      );

    setFieldValue(
      "serviceId",
      serviceId
    );

    setFieldValue(
      "locationId",
      ""
    );

    setFieldValue(
      "professionalId",
      ""
    );

    if (service) {
      setFieldValue(
        "durationMinutes",
        service.durationMinutes
      );

      setFieldValue(
        "value",
        service.price || ""
      );

      const eligibleLocations =
        getEligibleLocations(
          serviceId
        );

      if (
        eligibleLocations.length === 1
      ) {
        const locationId =
          eligibleLocations[0].id;

        setFieldValue(
          "locationId",
          locationId
        );

        const eligibleProfessionals =
          getEligibleProfessionals(
            serviceId,
            locationId
          );

        if (
          eligibleProfessionals.length === 1
        ) {
          setFieldValue(
            "professionalId",
            eligibleProfessionals[0].id
          );
        }
      }
    }
  };


  const getEligibleLocations = serviceId => {
    if (!serviceId) return [];

    const professionalIds =
      serviceProfessionalLinks
        .filter(
          link =>
            Number(link.serviceId) ===
            Number(serviceId)
        )
        .map(link =>
          Number(link.userId)
        );

    const locationIds =
      professionalLocationLinks
        .filter(link =>
          professionalIds.includes(
            Number(link.professionalId)
          )
        )
        .map(link =>
          Number(link.locationId)
        );

    return locations.filter(
      location =>
        location.active !== false &&
        locationIds.includes(
          Number(location.id)
        )
    );
  };


  const getEligibleProfessionals = (
    serviceId,
    locationId
  ) => {
    if (!serviceId) {
      return [];
    }

    const serviceProfessionalIds =
      serviceProfessionalLinks
        .filter(
          link =>
            Number(link.serviceId) ===
            Number(serviceId)
        )
        .map(link =>
          Number(link.userId)
        );

    if (!locationId) {
      return professionals.filter(
        professional =>
          serviceProfessionalIds.includes(
            Number(professional.id)
          )
      );
    }

    const locationProfessionalIds =
      professionalLocationLinks
        .filter(
          link =>
            Number(link.locationId) ===
            Number(locationId)
        )
        .map(link =>
          Number(link.professionalId)
        );

    return professionals.filter(
      professional =>
        serviceProfessionalIds.includes(
          Number(professional.id)
        ) &&
        locationProfessionalIds.includes(
          Number(professional.id)
        )
    );
  };

  const handleClose = () => {
    setAppointment(initialState);
    setSelectedContact(null);
    onClose();
  };

  const handleSaveAppointment = async values => {
    const activeLocations =
      locations.filter(
        location =>
          location.active !== false
      );

    if (activeLocations.length) {
      const eligibleLocations =
        getEligibleLocations(
          values.serviceId
        );

      if (!eligibleLocations.length) {
        toast.error(
          "Este serviço ainda não possui unidade e profissionais vinculados."
        );
        return;
      }

      if (!values.locationId) {
        toast.error(
          "Selecione a unidade"
        );
        return;
      }

      const eligibleProfessionals =
        getEligibleProfessionals(
          values.serviceId,
          values.locationId
        );

      const validProfessional =
        eligibleProfessionals.some(
          professional =>
            Number(professional.id) ===
            Number(values.professionalId)
        );

      if (!validProfessional) {
        toast.error(
          "Selecione um profissional disponível nesta unidade."
        );
        return;
      }
    }

    const start = moment(`${values.startDate} ${values.startTime}`, "YYYY-MM-DD HH:mm");
    const end = start.clone().add(values.durationMinutes || 30, "minutes");

    const payload = {
      skipAvailabilityRules: true,
      contactId: selectedContact ? selectedContact.id : null,
      professionalId: values.professionalId,
      serviceId: values.serviceId || null,
      locationId: values.locationId || null,
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      status: values.status,
      notes: values.notes,
      value: values.value || null
    };

    try {
      if (appointmentId) {
        await api.put(`/appointments/${appointmentId}`, payload);
      } else {
        await api.post("/appointments", payload);
      }
      toast.success(appointmentId ? "Agendamento atualizado" : "Agendamento criado");
      if (typeof reload === "function") reload();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{appointmentId ? "Editar agendamento" : "Novo agendamento"}</DialogTitle>
      <Formik
        initialValues={appointment}
        enableReinitialize
        validationSchema={AppointmentSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveAppointment(values);
            actions.setSubmitting(false);
          }, 300);
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={contactOptions}
                    getOptionLabel={option => `${option.name} (${option.number})`}
                    value={selectedContact}
                    onChange={(e, value) => setSelectedContact(value)}
                    onInputChange={(e, value) => handleSearchContact(value)}
                    renderInput={params => (
                      <TextField {...params} label="Cliente" variant="outlined" fullWidth />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                  >
                    <InputLabel>
                      Serviço
                    </InputLabel>

                    <Select
                      value={values.serviceId}
                      label="Serviço"
                      onChange={e =>
                        handleServiceChange(
                          e.target.value,
                          setFieldValue
                        )
                      }
                    >
                      {services.map(service => (
                        <MenuItem
                          key={service.id}
                          value={service.id}
                        >
                          {service.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {locations.some(
                  location =>
                    location.active !== false
                ) && (
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      disabled={!values.serviceId}
                    >
                      <InputLabel>
                        Unidade
                      </InputLabel>

                      <Select
                        value={values.locationId}
                        label="Unidade"
                        onChange={e => {
                          setFieldValue(
                            "locationId",
                            e.target.value
                          );

                          setFieldValue(
                            "professionalId",
                            ""
                          );
                        }}
                      >
                        {getEligibleLocations(
                          values.serviceId
                        ).map(location => (
                          <MenuItem
                            key={location.id}
                            value={location.id}
                          >
                            {location.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  sm={
                    locations.some(
                      location =>
                        location.active !== false
                    )
                      ? 6
                      : 12
                  }
                >
                  <FormControl
                    variant="outlined"
                    fullWidth
                    disabled={
                      !values.serviceId ||
                      (
                        locations.some(
                          location =>
                            location.active !== false
                        ) &&
                        !values.locationId
                      )
                    }
                  >
                    <InputLabel>
                      Profissional
                    </InputLabel>

                    <Field
                      as={Select}
                      name="professionalId"
                      label="Profissional"
                      value={
                        values.professionalId
                      }
                    >
                      {getEligibleProfessionals(
                        values.serviceId,
                        values.locationId
                      ).map(professional => (
                        <MenuItem
                          key={professional.id}
                          value={professional.id}
                        >
                          {professional.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    name="startDate"
                    label="Data"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    name="startTime"
                    label="Horário"
                    type="time"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    name="durationMinutes"
                    label="Duração (min)"
                    type="number"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    as={TextField}
                    name="value"
                    label="Valor (R$)"
                    type="number"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Field as={Select} name="status" label="Status" value={values.status}>
                      <MenuItem value="agendado">Agendado</MenuItem>
                      <MenuItem value="confirmado">Confirmado</MenuItem>
                      <MenuItem value="em_atendimento">Em atendimento</MenuItem>
                      <MenuItem value="finalizado">Finalizado</MenuItem>
                      <MenuItem value="cancelado">Cancelado</MenuItem>
                      <MenuItem value="nao_compareceu">Não compareceu</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="notes"
                    label="Observação"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                className={classes.btnWrapper}
              >
                {appointmentId ? "Salvar" : "Criar"}
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AppointmentModal;
